import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ICompany, IdentityWebApiClient, ListUsersForViewAsRequest, User, ListCompaniesForViewAsRequest } from 'identity-webapi-client';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { AuthService, StateService } from 'src/app/services';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'view-as',
  templateUrl: './view-as.component.html',
  styleUrls: ['./view-as.component.scss']
})
export class ViewAsComponent implements OnInit {
  @Output() onShow: EventEmitter<any> = new EventEmitter();
  @Output() onHide: EventEmitter<any> = new EventEmitter();

  @ViewChild('userInput') userInput: ElementRef | undefined;
  @ViewChild('companyInput') companyInput: ElementRef | undefined;

  darkTheme: boolean = false;

  loadingViewAs = false;
  disabledRevert = true;
  disabledViewAs = false;

  loadingCompanies = false;
  companyAutocomplete = new FormControl();
  companies: Observable<ICompany[]> | undefined;
  selectedCompany: ICompany | undefined = undefined;
  companySearch = '';

  loadingUsers = false;
  userAutocomplete = new FormControl();
  users: Observable<User[]> | undefined;
  selectedUser: User | undefined = undefined;
  userSearch = '';

  public get showLoadingBar() {
    return this.loadingUsers || this.loadingCompanies || this.loadingViewAs;
  }

  urlViewedAs: string = '';

  constructor(
    private authService: AuthService,
    private identityWebApiClient: IdentityWebApiClient,
    private stateService: StateService,
    private userService: UserService) {
  }

  ngOnInit(): void {
    this.stateService.state$.subscribe((state) => {
      this.darkTheme = state.useDarkTheme;
    });

    this.loadCompanies();
    this.companyAutocomplete.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      map(value => {
        if (typeof value === 'string' || value instanceof String) {
          return value as string;
        }

        this.selectedCompany = value;
        this.clearUserAutocomplete().then(() => {
          this.userInput?.nativeElement.focus();
        });
      }),
      filter((value) => typeof value === 'string'),
      map(value => {
        this.companySearch = value?.toLowerCase() ?? '';
        this.loadCompanies();
      })).subscribe();

    this.loadUsers();
    this.userAutocomplete.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      map(value => {
        if (typeof value === 'string' || value instanceof String) {
          return value as string;
        }

        this.selectedUser = value;
        this.disabledViewAs = this.shouldDisableViewAs();
      }),
      filter((value) => typeof value === 'string'),
      map(value => {
        this.userSearch = value?.toLowerCase() ?? '';
        this.loadUsers();
      })).subscribe();

    this.authService.userSubject.subscribe((user) => {
      this.disabledRevert = !this.authService.getOriginalUserId();
      this.disabledViewAs = this.shouldDisableViewAs();

      if (this.authService.getOriginalUserId() && !this.selectedUser) {
        this.userSearch = this.authService.getUserName();
        this.userAutocomplete.setValue(this.authService.getUserName());
      }
    });
  }

  displayCompany(company: ICompany) {
    return company?.name;
  }

  displayUser(user: User | string) {
    if (typeof user === 'string' || user instanceof String) {
      return user;
    }

    return user?.email;
  }

  onClickClearCompanyAutocomplete() {
    this.clearCompanyAutocomplete().then(() => {
      this.companyInput?.nativeElement.focus();
    });
    this.clearUserAutocomplete();
  }

  clearCompanyAutocomplete() {
    this.companyAutocomplete.setValue('');
    this.selectedCompany = undefined;
    this.loadUsers();
    return this.loadCompanies();
  }

  onClickClearUserAutocomplete() {
    this.clearUserAutocomplete().then(() => {
      this.userInput?.nativeElement.focus();
    });
  }

  clearUserAutocomplete() {
    this.userAutocomplete.setValue('');
    this.selectedUser = undefined;
    this.disabledViewAs = this.shouldDisableViewAs();
    return this.loadUsers();
  }

  onCompanyKeyUp(event: KeyboardEvent) {
    if ((event.key === 'Enter') && this.selectedCompany) {
      this.userInput?.nativeElement.focus();
    }
  }

  onUserKeyUp(event: KeyboardEvent) {
    if ((event.key === 'Enter') && this.selectedUser && !this.shouldDisableViewAs()) {
      this.onClickViewAsUser();
    }
  }

  shouldDisableViewAs() {
    return !this.selectedUser || this.selectedUser?.id == this.authService.getUserId();
  }

  loadUsers() {
    let request = new ListUsersForViewAsRequest()
    request.pageNumber = 1;
    request.pageSize = 50;
    request.companyId = this.selectedCompany?.id;
    request.search = this.userSearch;

    this.loadingUsers = true;
    return this.identityWebApiClient.listUsersForViewAs(request).then(response => {
      this.users = of(response?.items ?? []);
      this.loadingUsers = false;
    }, error => {
      this.loadingUsers = false;
    });
  }

  loadCompanies() {
    let request = new ListCompaniesForViewAsRequest();
    request.pageNumber = 1;
    request.pageSize = 50;
    request.search = this.companySearch;

    this.loadingCompanies = true;
    return this.identityWebApiClient.listCompaniesForViewAs(request).then(response => {
      this.companies = of(response?.items ?? []);
      this.loadingCompanies = false;
    }, error => {
      this.loadingCompanies = false;
    });
  }

  onClickViewAsUser() {
    if (!this.selectedUser?.id || this.loadingViewAs) {
      return;
    }

    this.loadingViewAs = true;
    this.userService.viewAsUser(this.selectedUser.id).then(() => {
      this.loadingViewAs = false;
    }, error => {
      this.loadingViewAs = false;
    });
  }

  onClickRevert() {
    if (!this.authService.getOriginalUserId()) {
      return;
    }

    this.loadingViewAs = true;
    this.clearUserAutocomplete();
    this.clearCompanyAutocomplete();

    this.userService.viewAsUser(this.authService.getOriginalUserId()).then(() => {
      this.loadingViewAs = false;
    }, error => {
      this.loadingViewAs = false;
    });
  }
}
